import type { StoreHit } from '@/lib/algolia/stores';

import type { StoreHoursStatus, StoreOpenStatus } from './hours';

export type BoutiqueGeolocationSource = Pick<
  StoreHit,
  '_geoloc' | 'latitude' | 'longitude' | 'lat' | 'long'
>;

/**
 * Resolve the most precise boutique coordinates available on a store hit.
 *
 * Algolia `_geoloc` is indexed for geo search and can be rounded (e.g. 5 decimals
 * in PRE). Production store records also expose full-precision `latitude` /
 * `longitude` (or legacy `lat` / `long`) from Yext — same source Breitling uses
 * for Get Directions links.
 */
export function resolveBoutiqueGeolocation(
  store: BoutiqueGeolocationSource
): { lat: number; lng: number } | undefined {
  const { latitude, longitude, lat, long, _geoloc } = store;

  if (typeof latitude === 'number' && typeof longitude === 'number') {
    return { lat: latitude, lng: longitude };
  }

  if (typeof lat === 'number' && typeof long === 'number') {
    return { lat, lng: long };
  }

  if (typeof _geoloc?.lat === 'number' && typeof _geoloc?.lng === 'number') {
    return { lat: _geoloc.lat, lng: _geoloc.lng };
  }

  return undefined;
}

export function buildBoutiqueAddressLines(params: {
  countryCode: string;
  localeSlug: string;
  street: string;
  street2?: string;
  city: string;
  region?: string;
  postalCode: string;
}): string[] {
  const { countryCode, localeSlug, street, street2, city, region, postalCode } = params;
  const showJpAddress = countryCode === 'JP' && localeSlug === 'ja';
  const street2Lines = street2 ? [street2] : [];

  if (showJpAddress) {
    return [`${postalCode} ${region ?? ''} ${city}`.trim(), street, ...street2Lines];
  }

  return [street, ...street2Lines, `${postalCode} ${city}`.trim()];
}

export function buildBoutiqueMapsUrl(
  store: BoutiqueGeolocationSource,
  street: string,
  city: string
): string {
  const geoloc = resolveBoutiqueGeolocation(store);

  if (geoloc) {
    // Match Breitling getGoogleMapAddress — full double precision, comma encoded as %2C
    return `https://www.google.com/maps/dir/?api=1&destination=${geoloc.lat}%2C${geoloc.lng}`;
  }

  const addressQuery = `${street}, ${city}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressQuery)}`;
}

export function getBoutiqueOpenStatusLabel(
  status: StoreHoursStatus,
  labels: { open: string; closed: string; temporary: string }
): string {
  if (status === 'open') {
    return labels.open;
  }

  if (status === 'closed') {
    return labels.closed;
  }

  return labels.temporary;
}

export function getBoutiqueHoursMessage(
  openStatus: StoreOpenStatus,
  translate: {
    opensUntil: (values: { time: string }) => string;
    opensAt: (values: { time: string; day: string }) => string;
    weekday: (weekday: string) => string;
  }
): string {
  if (openStatus.status === 'open') {
    const closingTime = openStatus.hoursLabel.split(' - ')[1] ?? openStatus.hoursLabel;
    return translate.opensUntil({ time: closingTime });
  }

  if (openStatus.hoursLabel && openStatus.nextOpenWeekday) {
    const openingTime = openStatus.hoursLabel.split(' - ')[0] ?? openStatus.hoursLabel;
    const day = translate.weekday(openStatus.nextOpenWeekday);
    return translate.opensAt({ time: openingTime, day });
  }

  return '';
}
